import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type BreadcrumbState = {
  managerId: string | null;
  label: string | null;
};

type AccountManagerProfileBreadcrumbContextValue = {
  managerId: string | null;
  label: string | null;
  setBreadcrumb: (managerId: string, label: string) => void;
  clearBreadcrumb: () => void;
};

const AccountManagerProfileBreadcrumbContext =
  createContext<AccountManagerProfileBreadcrumbContextValue | null>(null);

export function AccountManagerProfileBreadcrumbProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [state, setState] = useState<BreadcrumbState>({
    managerId: null,
    label: null,
  });

  const setBreadcrumb = useCallback((managerId: string, label: string) => {
    setState({ managerId, label });
  }, []);

  const clearBreadcrumb = useCallback(() => {
    setState({ managerId: null, label: null });
  }, []);

  const value = useMemo(
    () => ({
      managerId: state.managerId,
      label: state.label,
      setBreadcrumb,
      clearBreadcrumb,
    }),
    [state.managerId, state.label, setBreadcrumb, clearBreadcrumb],
  );

  return (
    <AccountManagerProfileBreadcrumbContext.Provider value={value}>
      {children}
    </AccountManagerProfileBreadcrumbContext.Provider>
  );
}

export function useAccountManagerProfileBreadcrumb(): AccountManagerProfileBreadcrumbContextValue {
  const ctx = useContext(AccountManagerProfileBreadcrumbContext);
  if (!ctx) {
    throw new Error(
      "useAccountManagerProfileBreadcrumb must be used within AccountManagerProfileBreadcrumbProvider",
    );
  }
  return ctx;
}
